// ================================================================
// AiUsagePanel — «AI-Nutzung» über ALLE Firmen (Plattform-Auswertung)
//
// Verbrauchszahlen je Firma: Voice-Sitzungen + Minuten, Übersetzungen
// (WhatsApp/E-Mail). Klick auf eine Firma klappt die Mitarbeiter auf.
// Nur Zahlen — Inhalte gehören ins Panel «Assistenten-Gespräche».
// Quelle: /api/platform/ai-usage (AiUsage-Events, Recht platform:usage:read).
// ================================================================
import './ai-usage.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml, delegate } from '../../utils/dom.js';
import { getAiUsage } from '../../core/platformUsage.js';

let overlay = null;

const s = {
  days: 30,
  orgs: [],
  loading: false,
  error: '',
  open: new Set(), // aufgeklappte organizationIds
};

const DAY_CHOICES = [7, 30, 90, 365];

export function openAiUsagePanel() {
  if (overlay) return;
  overlay = document.createElement('div');
  overlay.className = 'au-overlay';
  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';
  delegate(overlay, '[data-action]', 'click', onAction);
  s.open = new Set();
  load();
}

function close() {
  if (!overlay) return;
  overlay.remove();
  overlay = null;
  document.body.style.overflow = '';
}

async function load() {
  s.loading = true; s.error = ''; draw();
  try {
    const d = await getAiUsage(s.days);
    s.orgs = d.orgs || [];
  } catch (e) {
    s.error = e.message;
  } finally {
    s.loading = false; draw();
  }
}

function onAction(e, el) {
  const a = el.dataset.action;
  if (a === 'close') return close();
  if (a === 'days') { s.days = Number(el.dataset.days) || 30; return load(); }
  if (a === 'toggle-org') {
    const id = el.dataset.id;
    if (s.open.has(id)) s.open.delete(id); else s.open.add(id);
    return draw();
  }
}

// ── Darstellung ──
const mins = (seconds) => Math.round((Number(seconds) || 0) / 60);

function kindCells(byKind) {
  const voice = byKind.voice_session || { quantity: 0, events: 0 };
  const wa = byKind.translation_whatsapp || { quantity: 0, events: 0 };
  const mail = byKind.translation_email || { quantity: 0, events: 0 };
  return `
    <td class="au-num">${voice.events ? `${voice.events} <span class="au-dim">·</span> ${mins(voice.quantity)} ${escapeHtml(t('platformUsage.minutes'))}` : '<span class="au-dim">—</span>'}</td>
    <td class="au-num">${wa.quantity ? Math.round(wa.quantity) : '<span class="au-dim">—</span>'}</td>
    <td class="au-num">${mail.quantity ? Math.round(mail.quantity) : '<span class="au-dim">—</span>'}</td>`;
}

// users kommen je (user × kind) — für die Anzeige je user gruppieren
function groupUsers(users) {
  const map = new Map();
  for (const u of users) {
    const key = u.userId || '—';
    const row = map.get(key) || { userName: u.userName, byKind: {} };
    const agg = (row.byKind[u.kind] ||= { quantity: 0, events: 0 });
    agg.quantity += u.quantity;
    agg.events += u.events;
    map.set(key, row);
  }
  return [...map.values()];
}

function orgRows(org) {
  const open = s.open.has(org.organizationId);
  const head = `
    <tr class="au-row" data-action="toggle-org" data-id="${escapeHtml(org.organizationId)}">
      <td class="au-org">
        <span class="material-symbols-rounded au-chev ${open ? 'open' : ''}">chevron_right</span>
        ${escapeHtml(org.organizationName)}
      </td>
      ${kindCells(org.byKind)}
    </tr>`;
  if (!open) return head;
  const users = groupUsers(org.users).map((u) => `
    <tr class="au-userrow">
      <td class="au-user">${escapeHtml(u.userName)}</td>
      ${kindCells(u.byKind)}
    </tr>`).join('');
  return head + users;
}

function draw() {
  if (!overlay) return;
  const body = s.loading
    ? `<div class="au-muted">${escapeHtml(t('dashboard.loading'))}</div>`
    : s.error
      ? `<div class="au-error">${escapeHtml(s.error)}</div>`
      : !s.orgs.length
        ? `<div class="au-muted">${escapeHtml(t('platformUsage.empty'))}</div>`
        : `
          <table class="au-table">
            <thead>
              <tr>
                <th>${escapeHtml(t('platformUsage.colCompany'))}</th>
                <th class="au-num">${escapeHtml(t('platformUsage.colVoice'))}</th>
                <th class="au-num">${escapeHtml(t('platformUsage.colWa'))}</th>
                <th class="au-num">${escapeHtml(t('platformUsage.colMail'))}</th>
              </tr>
            </thead>
            <tbody>${s.orgs.map(orgRows).join('')}</tbody>
          </table>`;

  overlay.innerHTML = `
    <div class="au-backdrop" data-action="close"></div>
    <div class="au-panel">
      <div class="au-head">
        <div class="au-head-title">${icon('monitoring', 20)}${escapeHtml(t('platformUsage.title'))}</div>
        <button class="au-icon-btn" data-action="close">${icon('close', 20)}</button>
      </div>
      <div class="au-body">
        <div class="au-subtitle">${escapeHtml(t('platformUsage.subtitle'))}</div>
        <div class="au-filters">
          ${DAY_CHOICES.map((d) => `
            <button class="au-chip ${s.days === d ? 'active' : ''}" data-action="days" data-days="${d}">
              ${escapeHtml(t('platformUsage.days', { n: d }))}
            </button>`).join('')}
        </div>
        ${body}
      </div>
    </div>`;
}
